import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useQuery
} from '@tanstack/react-query';

import { ELEMENTS_PRODUCTS_PROMOTION } from '@constants/ElementsLimit';

import API from '@api';
import { withSsrCache } from '../../ssrCache';

/**
 * Список акций /promotions. Пагинации на странице нет — все акции
 * тянутся сразу одним запросом (как в старом коде: expired / 30 штук).
 */
const PROMOTIONS_STALE_TIME = 5 * 60 * 1000;
const PROMOTIONS_GC_TIME = 10 * 60 * 1000;

export const promotionsQueryOptions = (
  params: Parameters<typeof API.getPromotions>[0] = {
    setOrder: 'expired',
    page: 1,
    setLimit: 30
  }
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['promotions', params],
      queryFn: () => API.getPromotions(params),
      staleTime: PROMOTIONS_STALE_TIME,
      gcTime: PROMOTIONS_GC_TIME
    }),
    'product'
  );

export const usePromotionsQuery = (
  params?: Parameters<typeof API.getPromotions>[0]
) => useQuery(promotionsQueryOptions(params));

/** Хвост «-353» и т.п. срезается (вынесено 1:1 из Promotion/component) */
export const normalizePromotionIdentify = (value?: string | null): string =>
  (value || '').replace(/-\d+$/, '');

/**
 * Разбор сегмента /promotions/:id — «353» (число) или «festival-kraba-353»
 * (identify + id). ЕДИНЫЙ источник для компонента и SSR-префетча.
 */
export const parsePromotionUrlData = (
  value: string
): { id: number | null; identify: string } => {
  if (/^\d+$/.test(value)) {
    return { id: Number(value), identify: '' };
  }

  const match = value.match(/^(.*?)-(\d+)$/);
  return {
    id: match ? Number(match[2]) : null,
    identify: normalizePromotionIdentify(match ? match[1] : value)
  };
};

export type PromotionByIdKeyParamsT = {
  id?: number;
  identify?: string;
  /** ?type= — активная рубрика акции ('' — все) */
  type: string;
  /** В запрос не передаётся (уходит cart token'ом), но входит в ключ */
  xStoreId?: number | string | null;
};

/**
 * Зеркало условных спредов из Promotion/component: наличие/отсутствие полей
 * id/identify в объекте ключа обязано совпасть байт-в-байт с префетчем.
 */
export const buildPromotionByIdKeyParams = (
  urlData: { id: number | null; identify: string },
  type: string | null,
  xStoreId: number | string | null
): PromotionByIdKeyParamsT => ({
  ...(urlData.id ? { id: urlData.id } : {}),
  ...(urlData.identify ? { identify: urlData.identify } : {}),
  type: type || '',
  xStoreId
});

/**
 * ЭТАП 2 — страница акции /promotions/:id (findPromotionByIdTemp) как
 * infinite-query. Раньше — императивный loadPromoInfo с setState-конкатом
 * страниц: SSR отдавал пустоту (return null). Страницы «Показать ещё»
 * копятся через fetchNextPage; прямой заход с ?page=N начинает с N-й
 * (initialPageParam), как раньше. xStoreId в ключе — замена mobx-reaction.
 */
export const promotionByIdInfiniteQueryOptions = (
  params: PromotionByIdKeyParamsT,
  initialPage = 1
) =>
  withSsrCache(
    infiniteQueryOptions({
      queryKey: ['promotionById', 'infinite', params],
      queryFn: ({ pageParam }) => {
        const { id, identify, type } = params;
        return API.getPromotionById({
          ...(id ? { id } : {}),
          ...(identify ? { identify } : {}),
          page: pageParam,
          type: type || '',
          setLimit: ELEMENTS_PRODUCTS_PROMOTION
        }).then(res => res.findPromotionByIdTemp);
      },
      initialPageParam: initialPage,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        // Из dev: считаем по НОМЕРУ страницы, а не по числу загруженных
        // товаров. При заходе сразу на ?page=N страницы 1..N-1 не грузились,
        // и счётчик «загружено < всего» оставался верным до самого конца —
        // страница догружалась бесконечно.
        const totalPages = Math.ceil(
          (lastPage.totalCount || 0) / ELEMENTS_PRODUCTS_PROMOTION
        );

        return lastPageParam < totalPages ? lastPageParam + 1 : undefined;
      },
      staleTime: PROMOTIONS_STALE_TIME,
      gcTime: PROMOTIONS_GC_TIME,
      // Смена магазина/рубрики не роняет страницу в пустоту на время
      // перезапроса
      placeholderData: keepPreviousData
    }),
    'product'
  );
